import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useDeliveryAuth } from '../context/DeliveryAuthContext';
import './DeliveryHistory.css';

const DeliveryHistory = () => {
  const { deliveryBoyData } = useDeliveryAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, today, week, month
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    cancelled: 0,
    totalEarnings: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!deliveryBoyData) return;

    const fetchDeliveries = async () => {
      try {
        const deliveriesQuery = query(
          collection(db, 'orders'),
          where('deliveryBoy.id', '==', deliveryBoyData.id),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(deliveriesQuery);
        const deliveriesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setDeliveries(deliveriesData);
        setFilteredDeliveries(deliveriesData);

        // Calculate stats
        const completed = deliveriesData.filter(d => d.status === 'delivered');
        const cancelled = deliveriesData.filter(d => d.status === 'cancelled');
        const earnings = completed.reduce((sum, order) => {
          const deliveryFee = order.deliveryCharge || 30;
          return sum + deliveryFee;
        }, 0);

        setStats({
          total: deliveriesData.length,
          completed: completed.length,
          cancelled: cancelled.length,
          totalEarnings: earnings
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching deliveries:', error);
        setLoading(false);
      }
    };

    fetchDeliveries();
  }, [deliveryBoyData]);

  useEffect(() => {
    // Apply filter
    const now = new Date();
    let filtered = [...deliveries];

    if (filter === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      filtered = deliveries.filter(d => {
        const orderDate = d.createdAt?.toDate();
        return orderDate >= today;
      });
    } else if (filter === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = deliveries.filter(d => {
        const orderDate = d.createdAt?.toDate();
        return orderDate >= weekAgo;
      });
    } else if (filter === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = deliveries.filter(d => {
        const orderDate = d.createdAt?.toDate();
        return orderDate >= monthAgo;
      });
    }

    setFilteredDeliveries(filtered);
  }, [filter, deliveries]);

  if (loading) {
    return (
      <div className="delivery-history">
        <div className="loading-spinner">Loading history...</div>
      </div>
    );
  }

  return (
    <div className="delivery-history">
      {/* Header */}
      <div className="history-header">
        <button onClick={() => navigate('/delivery/dashboard')} className="back-icon-btn">
          ← Back
        </button>
        <h1>Delivery History</h1>
        <div></div>
      </div>

      {/* Stats Overview */}
      <div className="history-stats">
        <div className="stat-box">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Deliveries</div>
        </div>
        <div className="stat-box completed">
          <div className="stat-value">{stats.completed}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-box earnings">
          <div className="stat-value">₹{stats.totalEarnings}</div>
          <div className="stat-label">Total Earnings</div>
        </div>
        <div className="stat-box cancelled">
          <div className="stat-value">{stats.cancelled}</div>
          <div className="stat-label">Cancelled</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Time
        </button>
        <button
          className={`filter-tab ${filter === 'today' ? 'active' : ''}`}
          onClick={() => setFilter('today')}
        >
          Today
        </button>
        <button
          className={`filter-tab ${filter === 'week' ? 'active' : ''}`}
          onClick={() => setFilter('week')}
        >
          This Week
        </button>
        <button
          className={`filter-tab ${filter === 'month' ? 'active' : ''}`}
          onClick={() => setFilter('month')}
        >
          This Month
        </button>
      </div>

      {/* Deliveries List */}
      <div className="history-content">
        {filteredDeliveries.length > 0 ? (
          <div className="history-list">
            {filteredDeliveries.map((delivery) => (
              <div key={delivery.id} className={`history-item ${delivery.status}`}>
                <div className="history-item-header">
                  <div>
                    <h3>Order #{delivery.orderNumber}</h3>
                    <p className="delivery-time">
                      {delivery.createdAt?.toDate?.()?.toLocaleDateString()} at{' '}
                      {delivery.createdAt?.toDate?.()?.toLocaleTimeString()}
                    </p>
                  </div>
                  <div className={`status-badge ${delivery.status}`}>
                    {delivery.status === 'delivered' && '✅ Delivered'}
                    {delivery.status === 'cancelled' && '❌ Cancelled'}
                    {delivery.status === 'outForDelivery' && '🚴 Delivering'}
                    {delivery.status === 'prepared' && '📦 Ready'}
                    {delivery.status === 'confirmed' && '⏳ Confirmed'}
                  </div>
                </div>

                <div className="history-item-details">
                  <div className="detail-row">
                    <span className="detail-label">Customer:</span>
                    <span className="detail-value">{delivery.customerName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">{delivery.address?.location}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Items:</span>
                    <span className="detail-value">{delivery.items?.length || 0} items</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Amount:</span>
                    <span className="detail-value amount">₹{delivery.totalAmount}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Payment:</span>
                    <span className="detail-value">
                      {delivery.paymentMethod === 'cod' ? '💵 COD' : '✅ Online'}
                    </span>
                  </div>
                  {delivery.status === 'delivered' && delivery.deliveryStatus?.deliveredAt && (
                    <div className="detail-row">
                      <span className="detail-label">Delivered at:</span>
                      <span className="detail-value">
                        {delivery.deliveryStatus.deliveredAt.toDate?.()?.toLocaleTimeString()}
                      </span>
                    </div>
                  )}
                </div>

                {delivery.status === 'delivered' && (
                  <div className="earnings-badge">
                    + ₹{delivery.deliveryCharge || 30} earned
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-history">
            <div className="no-history-icon">📦</div>
            <h3>No Deliveries Found</h3>
            <p>
              {filter === 'all'
                ? 'You have not made any deliveries yet'
                : `No deliveries found for ${filter === 'today' ? 'today' : filter === 'week' ? 'this week' : 'this month'}`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryHistory;
